import { useMemo } from 'react';
import { Tag, Typography, Tooltip, Button, Space } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import {
  SmartDataTableProps,
  SmartTableColumn,
} from '@src/components/SmartDataTable/types';
import { useStockLevelRender } from './useStockLevelRender';
import {
  CATEGORY_FILTER_OPTIONS,
  STOCK_LEVEL_FILTER_OPTIONS,
} from '../../constants';
import { useUserTimestampRender } from '@src/hooks/useUserTimestampRender';

const { Text } = Typography;

interface UseInsideStockItemsColumnsOptions {
  showCreatedAt?: boolean;
}

export const useInsideStockItemsColumns = (
  options: UseInsideStockItemsColumnsOptions = {}
): SmartTableColumn[] => {
  const { showCreatedAt = true } = options;
  const renderStockLevel = useStockLevelRender();
  const userTimestampRender = useUserTimestampRender();

  return useMemo(() => {
    const baseColumns: SmartTableColumn[] = [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'name',
        title: 'Item Name',
        dataIndex: 'name',
        sorter: true,
        minWidth: 200,
        ellipsis: true,
        render: (value: any) => (
          <Text style={{ fontWeight: 500, color: '#262626' }}>
            {value || '-'}
          </Text>
        ),
      },
      {
        key: 'category',
        title: 'Category',
        dataIndex: 'category',
        sorter: true,
        minWidth: 180,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: [...CATEGORY_FILTER_OPTIONS],
        render: (value: any) => {
          const category = CATEGORY_FILTER_OPTIONS.find(
            opt => opt.value === value
          );
          return (
            <Tag color="blue" style={{ margin: 0 }}>
              {category?.label || value || '-'}
            </Tag>
          );
        },
      },
      {
        key: 'unit',
        title: 'Unit',
        dataIndex: 'unit',
        sorter: false,
        minWidth: 100,
        render: (value: any) => (
          <Text style={{ color: '#595959' }}>{value || '-'}</Text>
        ),
      },
      {
        key: 'current_stock',
        title: 'Current Stock',
        dataIndex: 'current_stock',
        sorter: true,
        minWidth: 150,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: [...STOCK_LEVEL_FILTER_OPTIONS],
        render: (value: any, record: any) => renderStockLevel(value, record),
      },
      {
        key: 'supplier_info',
        title: 'Supplier Info',
        dataIndex: 'supplier',
        sorter: false,
        minWidth: 150,
        render: (supplier: any, record: any) => (
          <Tooltip title={supplier.name}>
            <div className="px-2">
              <Text style={{ fontWeight: 500, color: '#262626' }}>
                {supplier.name || '-'}
              </Text>
              {supplier.phone && (
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  +{supplier.phone || ''}
                </div>
              )}
            </div>
          </Tooltip>
        ),
      },
    ];

    // Conditionally add the created_at column with date range filter
    if (showCreatedAt) {
      baseColumns.push({
        key: 'created_at',
        title: 'User & Timestamp',
        dataIndex: 'created_at',
        sorter: false,
        minWidth: 180,
        filterable: true,
        filterType: 'daterange' as const,
        render: (value: any, record: any) => userTimestampRender(record),
      });
    }

    return baseColumns;
  }, [renderStockLevel, userTimestampRender, showCreatedAt]);
};
